import { useCallback, useMemo } from 'react';

import type { Team, TeamId } from '@/core/schemas';
import { activeTournamentId } from '@/features/tournament/useTournament';
import { subscribeTeams } from '@/infrastructure/repositories/teamRepository';
import { useLiveData, type LiveData } from '@/shared/hooks/useLiveData';

export function useTeams(): LiveData<Team[]> {
  const subscribe = useCallback(
    (onData: (value: Team[]) => void, onError: Parameters<typeof subscribeTeams>[2]) =>
      subscribeTeams(activeTournamentId, onData, onError),
    [],
  );

  return useLiveData<Team[]>(['teams', activeTournamentId], subscribe);
}

/** Lookup by id, for the many places that turn a team id into a name. */
export function useTeamLookup(teams: readonly Team[] | undefined) {
  return useMemo(() => {
    const map = new Map<TeamId, Team>((teams ?? []).map((team) => [team.id, team]));
    return (teamId: TeamId): Team | undefined => map.get(teamId);
  }, [teams]);
}
